<template>
  <div class="container my-5">
    <div class="cards-grid">
      <div
        v-for="(cell, idx) in cells"
        :key="idx"
        class="grid-item"
      >
        <!-- Mittleres Feld (null) → zentrale Karte -->
        <div
          v-if="cell === null"
          class="card-center text-center p-4 m-4 glass-container"
        >
          <h4 class="center-card-title  m-0">Air Fryer Verwendung</h4>
        </div>
        <div
          v-else-if="cell"
          class="card usage-card position-relative d-flex flex-column"
        >
          <div class="card-body d-flex flex-column">
            <h5 class="card-title text-start">
              <i :class="cell.icon + ' fs-3 me-2'"></i><br>
              {{ cell.title }}
            </h5>
            <ul class="mb-0 text-start flex-grow-1">
              <li
                v-for="(item, i) in cell.items"
                :key="i"
                :class="{ 'mb-2': i < cell.items.length - 1 }"
              >
                <i class="bi bi-check-circle me-2"></i>{{ item }}
              </li>
            </ul>
          </div>
        </div>

        <!-- Fallback für unerwartetes undefined -->
        <div v-else class="empty-cell"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const cards = [
  {
    title: 'Kleine Küche & wenig Platz',
    icon: 'bi bi-house-door',
    items: [
      'Kompakte Bauform mit geringer Breite und Tiefe',
      'Ideal bei wenig Arbeitsfläche',
      'Leicht zu verstauen und schnell einsatzbereit',
      'Ausreichendes Fassungsvermögen für kleine Portionen',
      'Gute Wahl für kleine Küchen oder Single-Haushalte'
    ]
  },
  {
    title: 'Singles & kleine Portionen',
    icon: 'bi bi-person',
    items: [
      'Kleiner Airfryer mit niedrigem Fassungsvermögen',
      'Ideal für Pommes, Nuggets, Gemüse oder Snacks',
      'Schnelle Zubereitung ohne großen Aufwand',
      'Geringerer Stromverbrauch durch kleinere Größe',
      'Einfach zu bedienen und alltagstauglich'
    ]
  },
  {
    title: 'Paare & Alltag',
    icon: 'bi bi-people',
    items: [
      'Mittleres Fassungsvermögen für zwei Personen',
      'Gut für schnelle Abendessen und Beilagen',
      'Ausgewogene Mischung aus Größe und Platzbedarf',
      'Geeignet für Pommes, Gemüse, Fisch oder Hähnchen',
      'Praktisch für regelmäßige Nutzung im Alltag'
    ]
  },
  {
    title: 'Familie & große Portionen',
    icon: 'bi bi-basket',
    items: [
      'Großes Fassungsvermögen für mehrere Personen',
      'Geeignet für Familiengerichte und größere Mengen',
      'Mehr Platz für Pommes, Hähnchen oder Gemüse',
      'Stabile Leistung für gleichmäßige Ergebnisse',
      'Ideal, wenn mehrere Portionen gleichzeitig gebraucht werden'
    ]
  },
  {
    title: 'Knusprige Pommes & Snacks',
    icon: 'bi bi-fire',
    items: [
      'Hohe Leistung für schnelle und knusprige Ergebnisse',
      'Gute Luftzirkulation für gleichmäßiges Frittieren',
      'Ideal für Pommes, Nuggets, Kroketten oder Gemüse',
      'Temperatur gut regelbar für verschiedene Speisen',
      'Perfekt, wenn Knusprigkeit besonders wichtig ist'
    ]
  },
  {
    title: 'Zwei Gerichte gleichzeitig',
    icon: 'bi bi-layout-split',
    items: [
      'Doppelkammer für zwei Speisen parallel',
      'Pommes und Hähnchen gleichzeitig zubereiten',
      'Praktisch für Familien oder unterschiedliche Vorlieben',
      'Getrennte Körbe mit eigenen Einstellungen',
      'Ideal, wenn komplette Mahlzeiten schneller fertig sein sollen'
    ]
  },
  {
    title: 'Vielseitig kochen',
    icon: 'bi bi-grid-3x3-gap',
    items: [
      'Mehrere Programme für verschiedene Gerichte',
      'Auch Backen, Grillen, Aufwärmen oder Dörren möglich',
      'Großer Temperaturbereich für flexible Nutzung',
      'Geeignet für kreative Rezepte statt nur Pommes',
      'Gute Wahl, wenn der Airfryer oft genutzt werden soll'
    ]
  },
  {
    title: 'Einfache Reinigung',
    icon: 'bi bi-droplet',
    items: [
      'Entnehmbare Schüssel für einfache Reinigung',
      'Antihaftbeschichtung gegen starke Rückstände',
      'Spülmaschinenfeste Teile sparen Zeit',
      'Weniger Fett und weniger Schmutz beim Kochen',
      'Ideal, wenn Pflege und Alltagstauglichkeit wichtig sind'
    ]
  },
  {
    title: 'Preisbewusst & Einsteiger',
    icon: 'bi bi-cash-coin',
    items: [
      'Solide Grundfunktionen ohne unnötige Extras',
      'Gute Wahl für gelegentliche Nutzung',
      'Einfaches Bedienkonzept statt zu vieler Programme',
      'Ausreichende Leistung für Pommes, Snacks und Beilagen',
      'Fokus auf gutes Preis-Leistungs-Verhältnis'
    ]
  }
];



// 3×3-Matrix mit null in der Mitte
const grid = computed(() => [
  [ cards[0], cards[1], cards[2] ],
  [ cards[3], null,      cards[4] ],
  [ cards[5], cards[6], cards[7] ]
])

const cells = computed(() => grid.value.flat())
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
@import 'https://cdn.jsdelivr.net/npm/bootstrap-icons@1.13.1/font/bootstrap-icons.css';


.cards-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(3, 1fr);
  align-items: stretch;
}


@media (min-width: 576px) and (max-width: 991.98px) {
  .cards-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 575.98px) {
  .cards-grid {
    grid-template-columns: 1fr;
    display: block;
  }
  .card-center {
    display: none !important;
  }
}

.grid-item {
  display: flex;
}
.grid-item > .usage-card,
.grid-item > .card-center {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.card-center {
  /* entferne Glass-Stile */
  background: linear-gradient(211deg, #60a5fa 0%, #0c53c6de 100%);
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  border: none;
  box-shadow: none;

  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  padding: 1rem;  
  margin: 1rem;   
  position: relative;
}


.center-card-title {
  position: relative;
  display: inline-block;      
  background: rgba(255,255,255,0.15);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 8px;
  padding: 0.5rem 1rem;
  transition: all 0.3s ease;
}

.center-card-title:hover {
  background: rgba(255,255,255,0.25);
  border-color: rgba(255,255,255,0.3);
  transform: translateY(-2px);
}

.usage-card {
  background-color: #F9FAFB;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease-in-out;
}
.usage-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
}

.card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.card-title {
  font-size: 1.125rem;
  font-weight: 500;
  color: #3d84f6;
  margin-bottom: 1rem;
}

ul {
  list-style: none;
  padding-left: 0;
}
li {
  font-size: 0.875rem;
  color: #4B5563;
  line-height: 1.4;
}
.empty-cell {
  flex: 1;
}


</style>


